import {
  useEffect,
  useRef,
  useState,
  type ChangeEvent,
  type ClipboardEvent,
  type CSSProperties,
  type FocusEvent,
  type InputHTMLAttributes,
} from 'react';
import { useField } from 'formik';
import { useTranslation } from 'react-i18next';
import { Input } from '@/components';
import { getFormFieldClasses } from '@/utility/utility';
import { FieldWrapper, type FieldWrapperConfig } from '@/form/FieldWrapper';
import styles from './MoneyField.module.css';
import {
  caretAfterReformat,
  formatMoneyInput,
  formatTypedMoney,
  moneySeparators,
  parseMoneyInput,
  plainMoneyToValue,
  readPastedMoney,
  readTypedMoney,
  toAmount,
} from '@/utility/money';

interface MoneyFieldProps
  extends
    FieldWrapperConfig,
    Omit<
      InputHTMLAttributes<HTMLInputElement>,
      'name' | 'type' | 'value' | 'defaultValue' | 'onChange' | 'onPaste'
    > {
  className?: string;
  placeholder?: string;
  /**
   * The unit shown at the end of the input, e.g. "$ / h" or "$ / mois"
   * (`useMoneyFormat().unit`). Read out with the field, never typed.
   */
  suffix?: string;
}

/**
 * A Formik money input with thousands separators in the app language:
 * typing 75000 shows "75,000" in English and "75 000" in French, cents
 * after "." or "," respectively ("75,000.50" / "75 000,50"), and the caret
 * stays where it was. The form value stays a plain number (`''` when
 * empty), so schemas and API payloads are unchanged. A pasted "75,000" or
 * "$75,000.50" is read as written, and on blur the amount settles to its
 * display form ("24.5" becomes "24.50"). Formatting rules: `utility/money`.
 * A `suffix` ("$ / h") sits inside the end of the input.
 */
export const MoneyField = ({
  name,
  label,
  infoText,
  labelHint,
  labelButton,
  ref,
  fieldClassName,
  showErrorSpace,
  disabled = false,
  className = '',
  placeholder,
  suffix,
  onBlur,
  ...inputProps
}: MoneyFieldProps) => {
  const [field, meta, helpers] = useField<number | string | null>(name);
  const { i18n } = useTranslation();
  const language = i18n.language;
  const [text, setText] = useState(() =>
    formatMoneyInput(field.value, language)
  );
  const lastLanguage = useRef(language);

  // Follow a value set from outside (prefill, reset) and a language switch.
  useEffect(() => {
    const languageChanged = lastLanguage.current !== language;
    const previousLanguage = lastLanguage.current;
    lastLanguage.current = language;
    setText((current) =>
      languageChanged ||
      parseMoneyInput(current, previousLanguage) !== toAmount(field.value)
        ? formatMoneyInput(field.value, language)
        : current
    );
  }, [field.value, language]);

  const apply = (
    input: HTMLInputElement,
    typed: string,
    caret: number
  ): void => {
    const plain = readTypedMoney(typed, language);
    const next = formatTypedMoney(plain, language);
    const nextCaret = caretAfterReformat(typed, caret, next, language);
    // Write the DOM first so React finds nothing to change and keeps the
    // caret where we put it.
    input.value = next;
    input.setSelectionRange(nextCaret, nextCaret);
    setText(next);
    void helpers.setValue(plainMoneyToValue(plain));
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const input = event.target;
    apply(input, input.value, input.selectionStart ?? input.value.length);
  };

  const handlePaste = (event: ClipboardEvent<HTMLInputElement>) => {
    const pasted = event.clipboardData.getData('text');
    if (!pasted) return;
    event.preventDefault();
    const input = event.currentTarget;
    const start = input.selectionStart ?? text.length;
    const end = input.selectionEnd ?? text.length;
    const insert = readPastedMoney(pasted).replace(
      '.',
      moneySeparators(language).decimal
    );
    apply(
      input,
      text.slice(0, start) + insert + text.slice(end),
      start + insert.length
    );
  };

  const handleBlur = (event: FocusEvent<HTMLInputElement>) => {
    field.onBlur(event);
    onBlur?.(event);
    setText(formatMoneyInput(field.value, language));
  };

  const suffixId = `${name}-unit`;
  const input = (
    <Input
      autoComplete="off"
      {...inputProps}
      id={name}
      name={name}
      type="text"
      inputMode="decimal"
      value={text}
      onChange={handleChange}
      onPaste={handlePaste}
      onBlur={handleBlur}
      placeholder={placeholder}
      disabled={disabled}
      className={getFormFieldClasses({ className, disabled, meta })}
      error={meta.touched && meta.error ? meta.error : undefined}
      aria-describedby={suffix ? suffixId : inputProps['aria-describedby']}
    />
  );

  return (
    <FieldWrapper
      ref={ref}
      name={name}
      label={label}
      infoText={infoText}
      labelHint={labelHint}
      labelButton={labelButton}
      disabled={disabled}
      errorMeta={meta}
      fieldClassName={fieldClassName}
      showErrorSpace={showErrorSpace}
    >
      {suffix ? (
        <div
          className={styles.withSuffix}
          style={{ '--money-suffix-chars': suffix.length } as CSSProperties}
        >
          {input}
          <span id={suffixId} className={styles.suffix}>
            {suffix}
          </span>
        </div>
      ) : (
        input
      )}
    </FieldWrapper>
  );
};
